import { createSlice, PayloadAction, createAction, createSelector } from '@reduxjs/toolkit';
import {
    GrammarContent,
    GrammarSession,
    CreateGrammarSessionRequest,
    CompleteGrammarSessionRequest,
    GrammarSessionData,
    GrammarContentData,
    GrammarProgressData
} from '@/services/grammar/types';
import { ContentFilters, PaginatedResponse } from '@/services/listening/types';
import { START_PAGE } from '@/common/constants';
import { RootState } from '@/store';

interface GrammarState {
    // Content Management
    contents: GrammarContent[];
    content: GrammarContent | null;
    contentLoading: boolean;
    contentError: string | null;

    // Session Management
    session: GrammarSession | null;
    sessionLoading: boolean;
    sessionError: string | null;
    isCreatingSession: boolean;
    isCompletingSession: boolean;

    // General Loading States
    loading: boolean;
    error: string | null;

    // Feedback
    feedback: any | null;

    // Pagination & Filters
    filters: ContentFilters;
    totalContents: number;
    currentPage: number;
}

const initialState: GrammarState = {
    // Content Management
    contents: [],
    content: null,
    contentLoading: false,
    contentError: null,

    // Session Management
    session: null,
    sessionLoading: false,
    sessionError: null,
    isCreatingSession: false,
    isCompletingSession: false,

    // General Loading States
    loading: false,
    error: null,

    // Feedback
    feedback: null,

    // Pagination & Filters
    filters: {},
    totalContents: 0,
    currentPage: START_PAGE,
};

// Async Actions for Contents
export const fetchGrammarContentsRequest = createAction<ContentFilters | undefined>(
    'grammar/fetchGrammarContentsRequest',
);
export const fetchGrammarContentsSuccess = createAction<PaginatedResponse<GrammarContent>>(
    'grammar/fetchGrammarContentsSuccess',
);
export const fetchGrammarContentsFailure = createAction<string>('grammar/fetchGrammarContentsFailure');

const grammarSlice = createSlice({
    name: 'grammar',
    initialState,
    reducers: {
        // Pagination & Filters
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
        setFilters: (state, action) => {
            state.filters = action.payload;
        },

        // Content Actions
        fetchGrammarContentRequest: (state, action: PayloadAction<string>) => {
            state.contentLoading = true;
            state.contentError = null;
        },
        fetchGrammarContentSuccess: (state, action: PayloadAction<GrammarContent>) => {
            state.content = action.payload;
            state.contentLoading = false;
            state.contentError = null;
        },
        fetchGrammarContentFailure: (state, action: PayloadAction<string>) => {
            state.contentLoading = false;
            state.contentError = action.payload;
        },

        // Session Actions
        startGrammarSessionRequest: (state, action: PayloadAction<CreateGrammarSessionRequest>) => {
            state.sessionLoading = true;
            state.isCreatingSession = true;
            state.sessionError = null;
        },
        startGrammarSessionSuccess: (state, action: PayloadAction<GrammarSession>) => {
            state.session = action.payload;
            state.sessionLoading = false;
            state.isCreatingSession = false;
            state.sessionError = null;
        },
        startGrammarSessionFailure: (state, action: PayloadAction<string>) => {
            state.sessionLoading = false;
            state.isCreatingSession = false;
            state.sessionError = action.payload;
        },

        completeGrammarSessionRequest: (state, action: PayloadAction<{ sessionId: string; data: CompleteGrammarSessionRequest }>) => {
            state.sessionLoading = true;
            state.isCompletingSession = true;
            state.sessionError = null;
        },
        completeGrammarSessionSuccess: (state, action: PayloadAction<GrammarSession>) => {
            state.session = action.payload;
            state.sessionLoading = false;
            state.isCompletingSession = false;
            state.sessionError = null;
        },
        completeGrammarSessionFailure: (state, action: PayloadAction<string>) => {
            state.sessionLoading = false;
            state.isCompletingSession = false;
            state.sessionError = action.payload;
        },

        // Answer Submission
        submitGrammarAnswerRequest: (state, action: PayloadAction<any>) => {
            state.loading = true;
            state.error = null;
        },
        submitGrammarAnswerSuccess: (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = null;
        },
        submitGrammarAnswerFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },

        // Progress Actions
        fetchGrammarProgressRequest: (state, action: PayloadAction<string>) => {
            state.loading = true;
            state.error = null;
        },
        fetchGrammarProgressSuccess: (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = null;
        },
        fetchGrammarProgressFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },

        // Feedback Actions
        fetchGrammarFeedbackRequest: (state, action: PayloadAction<string>) => {
            state.loading = true;
            state.error = null;
        },
        fetchGrammarFeedbackSuccess: (state, action: PayloadAction<any>) => {
            state.feedback = action.payload;
            state.loading = false;
            state.error = null;
        },
        fetchGrammarFeedbackFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },

        // Reset State
        resetGrammarState: (state) => {
            return initialState;
        },

        // Session Management
        clearCurrentSession: (state) => {
            state.session = null;
            state.sessionError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchGrammarContentsRequest, (state, action) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchGrammarContentsSuccess, (state, action) => {
                state.contents = action.payload.results;
                state.totalContents = action.payload.count;
                state.loading = false;
                state.error = null;
            })
            .addCase(fetchGrammarContentsFailure, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

// Action Exports
export const {
    setCurrentPage,
    setFilters,
    fetchGrammarContentRequest,
    fetchGrammarContentSuccess,
    fetchGrammarContentFailure,
    startGrammarSessionRequest,
    startGrammarSessionSuccess,
    startGrammarSessionFailure,
    completeGrammarSessionRequest,
    completeGrammarSessionSuccess,
    completeGrammarSessionFailure,
    submitGrammarAnswerRequest,
    submitGrammarAnswerSuccess,
    submitGrammarAnswerFailure,
    fetchGrammarProgressRequest,
    fetchGrammarProgressSuccess,
    fetchGrammarProgressFailure,
    fetchGrammarFeedbackRequest,
    fetchGrammarFeedbackSuccess,
    fetchGrammarFeedbackFailure,
    resetGrammarState,
    clearCurrentSession,
} = grammarSlice.actions;

// Base Selectors
export const selectGrammarState = (state: RootState) => state.grammar;

// Content Selectors
export const selectGrammarContentData = createSelector(
    [selectGrammarState],
    (grammarState): GrammarContentData => ({
        content: grammarState.content,
        loading: grammarState.contentLoading,
        error: grammarState.contentError,
    })
);

export const selectGrammarContents = createSelector(
    [selectGrammarState],
    (grammarState) => grammarState.contents
);

export const selectCurrentGrammarContent = createSelector(
    [selectGrammarState],
    (grammarState) => grammarState.content
);

// Session Selectors
export const selectGrammarSessionData = createSelector(
    [selectGrammarState],
    (grammarState): GrammarSessionData => ({
        session: grammarState.session,
        loading: grammarState.sessionLoading,
        error: grammarState.sessionError,
        isCreating: grammarState.isCreatingSession,
        isCompleting: grammarState.isCompletingSession,
        isActive: !!grammarState.session && !grammarState.isCompletingSession,
    })
);

export const selectCurrentGrammarSession = createSelector(
    [selectGrammarState],
    (grammarState) => grammarState.session
);

export const selectGrammarSessionProgress = createSelector(
    [selectGrammarState],
    (grammarState): GrammarProgressData => {
        const session = grammarState.session;
        if (!session) {
            return {
                accuracy: 0,
                questionsAnswered: 0,
                totalQuestions: 0,
                score: 0,
                timeSpent: 0,
            };
        }

        return {
            accuracy: session.accuracy,
            questionsAnswered: session.questions_answered,
            totalQuestions: session.total_questions,
            score: session.score,
            timeSpent: session.time_spent,
        };
    }
);

export const selectGrammarSessionStatus = createSelector(
    [selectGrammarState],
    (grammarState) => ({
        isCreating: grammarState.isCreatingSession,
        isCompleting: grammarState.isCompletingSession,
        isActive: !!grammarState.session && !grammarState.isCompletingSession,
        hasSession: !!grammarState.session,
    })
);

// Feedback Selectors
export const selectGrammarFeedback = createSelector(
    [selectGrammarState],
    (grammarState) => grammarState.feedback
);

// Loading & Error Selectors
export const selectGrammarLoading = createSelector(
    [selectGrammarState],
    (grammarState) => grammarState.loading || grammarState.contentLoading || grammarState.sessionLoading
);

export const selectGrammarError = createSelector(
    [selectGrammarState],
    (grammarState) => grammarState.error || grammarState.contentError || grammarState.sessionError
);

export default grammarSlice.reducer;
